import { createContext, useContext, useEffect, useState } from 'react';
import { CURRENT_USER, AUTH_TOKEN } from '../constants';

// Default user object when no user is logged in
const noUser = {
  id: null,
  name: '',
  email: '',
  allRoles: [],
  groups: [],
  token: null,
  isLoggedIn: false
};

// Create the context
export const UserContext = createContext(noUser);

// Custom hook for consuming the context
export const useUser = () => useContext(UserContext);

const getStoredUser = () => {
  const userStr = localStorage.getItem(CURRENT_USER);
  const userObj = userStr ? JSON.parse(userStr) : {};
  return {
    ...userObj,
    token: localStorage.getItem(AUTH_TOKEN)
  };
};

const userHasRequiredRoles =
  (user) =>
  (requiredRoles, allRequired = false) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    if (!user || !user.allRoles) return false;
    const userRoles = user.allRoles.map((role) => role.name);
    return allRequired
      ? requiredRoles.every((role) => userRoles.includes(role))
      : requiredRoles.some((role) => userRoles.includes(role));
  };

export const userCanAccessRoute = (requiredRoles) => {
  const user = getStoredUser();
  if (!user || !user.allRoles || !userHasRequiredRoles(user)(requiredRoles))
    return false;
  return true;
};

// Provider component
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(getStoredUser() || noUser);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isExternal, setIsExternal] = useState(false);

  const hasRoles = userHasRequiredRoles(user);

  useEffect(() => {
    const isValidUser = () => {
      const hasAnyRoles = user && user.allRoles && user.allRoles.length > 0;
      if (!hasAnyRoles) {
        return false;
      }

      return true;
    };
    const userExternal = hasRoles(['dashboard_external']);
    setIsLoggedIn(isValidUser());
    setIsExternal(userExternal);
  }, [user, hasRoles, setIsLoggedIn, setIsExternal]);

  const signIn = ({ token, userData }) => {
    localStorage.setItem(AUTH_TOKEN, token);
    localStorage.setItem(CURRENT_USER, JSON.stringify(userData));
    setUser(userData);
  };

  const signOut = () => {
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem(CURRENT_USER);
    setUser(noUser);
  };

  const contextValue = {
    user,
    hasRoles,
    isLoggedIn,
    isExternal,
    signIn,
    signOut
  };

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};
